import { useEffect, useState } from 'react';

import type { RecentChange, RecentChangesResponse } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { buildPath } from '@/app/routes';
import { useT } from '@/i18n/LocaleProvider';

export type RecentChangesProps = {
  lang?: string;
  onNavigate?: (slug: string) => void;
};

const relativeFormatter = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' });

export function RecentChanges({ lang, onNavigate }: RecentChangesProps) {
  const t = useT();
  const [changes, setChanges] = useState<RecentChange[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    wikiApiClient
      .getRecentChanges({ limit: 25, lang })
      .then((result: RecentChangesResponse) => {
        if (cancelled) return;
        setChanges(result.changes);
        setNextBefore(result.next_before ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err, t('recent_changes_error_load')));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t, lang]);

  function loadMore() {
    if (!nextBefore || isLoadingMore) return;

    let cancelled = false;
    setIsLoadingMore(true);

    wikiApiClient
      .getRecentChanges({ limit: 25, before: nextBefore, lang })
      .then((result: RecentChangesResponse) => {
        if (cancelled) return;
        setChanges((prev) => [...prev, ...result.changes]);
        setNextBefore(result.next_before ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err, t('recent_changes_error_load')));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingMore(false);
      });

    return () => {
      cancelled = true;
    };
  }

  return (
    <article className="note" data-testid="recent-changes">
      <header className="note-header">
        <div className="note-kicker">
          <span>{t('nav_recent_changes')}</span>
        </div>
        <h1>{t('recent_changes_title')}</h1>
      </header>

      {isLoading ? <div className="empty-note">{t('recent_changes_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}
      {!isLoading && !error && changes.length === 0 ? (
        <div className="empty-note">{t('recent_changes_empty')}</div>
      ) : null}

      {changes.length > 0 ? (
        <ul className="revision-list" data-testid="recent-changes-list">
          {changes.map((change) => (
            <li key={change.revision_id} className="revision-row">
              <a
                href={buildPath({ kind: 'reader', slug: change.slug, lang: change.language })}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate?.(change.slug);
                }}
              >
                <strong>{change.title}</strong>
                <time
                  dateTime={change.published_at}
                  title={new Date(change.published_at).toLocaleString('ru')}
                >
                  {relativeTime(change.published_at)}
                </time>
                <span title={change.author?.email ?? undefined}>
                  {change.author?.name ?? change.published_by}
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}

      {nextBefore && !isLoadingMore ? (
        <div className="empty-note">
          <button type="button" onClick={loadMore}>
            {t('recent_changes_load_more')}
          </button>
        </div>
      ) : null}
      {isLoadingMore ? (
        <div className="empty-note">{t('recent_changes_load_more_loading')}</div>
      ) : null}
    </article>
  );
}

function relativeTime(isoString: string): string {
  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) return isoString;

  const diffMs = date.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  if (Math.abs(diffSec) < 60) return relativeFormatter.format(diffSec, 'second');
  if (Math.abs(diffMin) < 60) return relativeFormatter.format(diffMin, 'minute');
  if (Math.abs(diffHour) < 24) return relativeFormatter.format(diffHour, 'hour');

  return relativeFormatter.format(diffDay, 'day');
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
